import {
  CISZA_MIN,
  MILCZY_MIN,
  ROZJAZD_PROG_S,
  opisBle,
  type PanelPulsFacts,
} from '@insolitum/fabrica-panel-core';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Clock,
  History,
  Loader2,
  ScrollText,
} from 'lucide-react';
import { useState } from 'react';

import { supabase } from '../../lib/supabase';

export type { PanelPulsFacts };

// =============================================================================
// CO PANEL PRODUKCYJNY MÓWI O SOBIE (migracje 497 i 543).
//
// DLACZEGO TEN PLIK ISTNIEJE. Puls panelu chodzi od `1.0.7` i od `1.0.7` NIKT
// GO NIE CZYTAŁ. Powód był jednoliterowy: `fabrica_panel_puls` scala ładunek
// operatorem `||`, czyli NA NAJWYŻSZYM POZIOMIE `device_info`, a karta
// urządzenia renderowała wyłącznie `device_info.runtime` — kształt ściany
// z migracji `249`. Dla panelu `runtime` jest `undefined`, więc warunek
// `license.device_info?.runtime && ...` gasił całą sekcję. Trzy stanowiska
// przez pięć dni zapisały 1145 wierszy telemetrii, których nie dało się
// zobaczyć inaczej niż zapytaniem SQL.
//
// DWA OKRESY ŻYCIA, DWA ŹRÓDŁA, DWA KOSZTY — i stąd podział tego ekranu:
//
//   • STAN TERAZ jedzie z `device_info`, które lista urządzeń i tak już
//     pobrała. Rysuje się natychmiast i nie kosztuje ani jednego zapytania.
//   • HISTORIA jedzie z `fabrica_panel_diagnostyka` (`543`) i jest ZA
//     KLIKNIĘCIEM. Nie z oszczędności — z uczciwości wobec pytania: „ile ma
//     restartów" jest pytaniem, które się ZADAJE, a nie tłem, które się ogląda.
//     Dwadzieścia cztery stanowiska znaczyłyby dwadzieścia cztery agregaty
//     puszczone dlatego, że ktoś wszedł na listę.
//
// LICZBA, KTÓRA JEST USTERKĄ, MA WYGLĄDAĆ NA USTERKĘ. Symulator zamiast wagi,
// klatki ponad budżetem, zegar rozjechany o minutę, ściśnięty układ — każde
// z nich czyta się z karty bez porównywania z niczym. Odwrotnie niż w panelu
// w hali (`S4`), sprawność MOŻE tu świecić na zielono: to jest ekran, na który
// patrzy się z zamiarem sprawdzenia, a nie kątem oka nad wagą.
// =============================================================================

// Kształt pulsu (`PanelPulsFacts`) mieszka w `pages/admin/devices/flota.ts`
// razem z progami — czytają go trzy ekrany, nie tylko ta karta.

/**
 * Wpis dziennika stanowiska (migracja `637`).
 *
 * DWA ZNACZNIKI CZASU I OBA SĄ POTRZEBNE: `o` pochodzi ze stanowiska i bywa
 * rozjechane o tyle, ile puls mierzy jako rozjazd zegara; `przyjeto` jest
 * nasze i zawsze prawdziwe. Dziennik, w którym nie da się odróżnić „kiedy się
 * stało" od „kiedy doszło", jest dziennikiem, którego nie da się uporządkować.
 */
interface WpisDziennika {
  id: number;
  o: string;
  przyjeto: string;
  /** `panel` — pierścień panelu; `agent` — dziennik nadzorcy agenta wagi. */
  zrodlo: string;
  waga: string;
  skad: string | null;
  co: string;
  slad: string | null;
  wersja: string | null;
}

/**
 * BIAŁA LISTA SOND — ta sama, co `CHECK` w migracji `638` i `enum Sonda`
 * w panelu. Trzy miejsca, jedna lista, i tak ma zostać: rozjazd nie daje
 * żadnego błędu, tylko „czekam" na ekranie aż do wygaśnięcia zlecenia.
 *
 * KOLEJNOŚĆ JEST WEDŁUG KOSZTU. `sonda_com` stoi ostatnia i jako jedyna ma
 * ostrzeżenie: zajmuje magistralę na minuty i przez ten czas nikt przy stole
 * nie waży. Reszta to odczyt pamięci i wraca w sekundę.
 */
const SONDY: { nazwa: string; opis: string; drogo?: boolean }[] = [
  { nazwa: 'token', opis: 'skąd panel ma token agenta (bez wartości)' },
  { nazwa: 'ekran', opis: 'DPI, dopasowanie układu, klasa urządzenia' },
  { nazwa: 'dziennik', opis: 'ostatnie zdarzenia panelu' },
  {
    nazwa: 'nadzorca',
    opis: 'ogony dzienników obok panelu: nadzorca agenta, aktualizator, instalator (Windows)',
  },
  { nazwa: 'porty', opis: 'porty szeregowe, które widzi system' },
  { nazwa: 'park', opis: 'wagi, które agent trzyma, i czy odpowiadają' },
  { nazwa: 'konfiguracja', opis: 'ustawienia agenta, kolejka zapisów' },
  { nazwa: 'sonda_com', opis: 'pełne przeszukanie magistral', drogo: true },
];

interface Zlecenie {
  id: string;
  sonda: string;
  stan: string;
  zlecono: string;
  odpowiedziano: string | null;
  odpowiedz: unknown;
  blad: string | null;
}

interface Diagnostyka {
  okno_dni: number;
  pulsow: number;
  pulsow_24h: number;
  pulsow_7d: number;
  pierwszy: string | null;
  ostatni: string | null;
  restartow_24h: number;
  restartow_7d: number;
  restartow_30d: number;
  ostatni_restart: string | null;
  czas_pracy_rekord_s: number | null;
  cisz_7d: number;
  cisza_max_s: number | null;
  cisze: { od: string; do: string; trwala_s: number }[];
  wersje: { wersja: string; kod_wersji: number | null; od: string; do: string; pulsow: number }[];
  na_agencie_7d: number;
  na_symulatorze_7d: number;
  ostatnio_na_agencie: string | null;
  p95_sredni_7d: number | null;
  p95_najgorszy_7d: number | null;
  pamiec_szczyt_mb: number | null;
  rozjazd_max_s: number | null;
  samotnosci_7d: number;
  przycichniec_7d: number;
  ekrany_7d: Record<string, number>;
}

// CISZA_MIN / MILCZY_MIN / ROZJAZD_PROG_S przyjeżdżają z `flota.ts`. Ta sama
// granica musi obowiązywać na karcie i na liście floty — próg policzony dwa
// razy rozjeżdża się przy pierwszej poprawce i pokazuje „Bije" obok wiersza
// oznaczonego jako milczący.

const NAZWA_EKRANU: Record<string, string> = {
  czuwanie: 'czuwanie',
  tablica: 'tablica zadań',
  wazenie: 'ważenie',
  wolna_waga: 'wolna waga',
  plotno: 'płótno partii',
  czyszczenie: 'czyszczenie',
  // Panel 1.0.79 (`S143`) nazywa też ekrany, które do 1.0.78 meldowały się
  // jako `tablica` — a stały na niej TYLKO w tym sensie, że podmieniały ją
  // pełnym ekranem. Cztery pierwsze są tu ważne nie dla urody: stanowisko
  // stojące na `szukanie_wagi` ma zamrożony widok hali w każdym wydaniu
  // starszym niż 1.0.79, więc ta nazwa jest diagnozą, nie ciekawostką.
  szukanie_wagi: 'szukanie wagi',
  szukanie_ble: 'urządzenia Bluetooth',
  kalibracja: 'kalibracja filtrów',
  zamkniecie: 'zamknięcie partii',
  pominiecie: 'pominięcie kroku',
  informacja: 'krok informacyjny',
  odliczanie: 'odliczanie',
  pomiar: 'pomiar',
  brak_ekranu: 'brak ekranu dla zadania',
  bez_licencji: 'panel bez licencji stanowiska',
};

/** `StanPolaczeniaAgenta` z panelu, po ludzku. */
const NAZWA_POLACZENIA: Record<string, string> = {
  polaczony: 'rozmawia',
  odmowaTokenu: 'agent odrzucił token',
  brakNasluchu: 'agent nie nasłuchuje',
  blad: 'zerwane',
  nieProbowano: 'nikt jeszcze nie pytał',
};

const NAZWA_SZCZEBLA: Record<string, string> = {
  pelnia: 'pełnia',
  przycichniecie: 'przycichnięcie',
  samotnosc: 'samotność',
};

/**
 * `StanAutomatu` z `aktualizacje.dart` (od `1.0.83`), po ludzku. Pięć stanów,
 * bo „nie ma tętna" i „tętno stare" to dwie różne wizyty: pierwsza zakłada
 * zadanie od nowa, druga pyta, co je zabiło.
 */
const NAZWA_AUTOMATU: Record<string, string> = {
  zyje: 'żyje (zadanie SYSTEM)',
  martwy: 'przestał się budzić',
  brak: 'nie ma',
  usluga: 'usługa systemd',
  nieDotyczy: 'nie dotyczy',
};

function czasTrwania(sekundy: number | null | undefined): string {
  if (sekundy == null || !Number.isFinite(sekundy)) return '—';
  if (sekundy < 60) return `${Math.round(sekundy)} s`;
  if (sekundy < 3600) return `${Math.round(sekundy / 60)} min`;
  if (sekundy < 86_400) {
    const h = Math.floor(sekundy / 3600);
    const m = Math.round((sekundy % 3600) / 60);
    return m > 0 ? `${h} h ${m} min` : `${h} h`;
  }
  const d = Math.floor(sekundy / 86_400);
  const h = Math.round((sekundy % 86_400) / 3600);
  return h > 0 ? `${d} d ${h} h` : `${d} d`;
}

function odKiedy(iso: string | null | undefined): string {
  if (!iso) return 'brak sygnału';
  const s = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (!Number.isFinite(s)) return 'brak sygnału';
  if (s < 90) return 'przed chwilą';
  return `${czasTrwania(s)} temu`;
}

function dataGodzina(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('pl-PL', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Fakt na karcie. `ton` niesie ocenę, nie ozdobę — `uwaga` znaczy „to jest
 * usterka i ktoś ma z nią pojechać do hali".
 */
function Fakt({
  etykieta,
  wartosc,
  ton = 'zwykly',
  pod,
}: {
  etykieta: string;
  wartosc: string;
  // `zly` dołożone w `1.0.70`: „agent odrzucił token" nie jest ostrzeżeniem,
  // tylko stanowiskiem, które NIE WAŻY. Amber obok amber niczego nie wyróżnia.
  ton?: 'zwykly' | 'uwaga' | 'dobry' | 'zly';
  pod?: string;
}) {
  const klasa =
    ton === 'zly'
      ? 'text-red-400'
      : ton === 'uwaga'
        ? 'text-amber-400'
        : ton === 'dobry'
          ? 'text-emerald-400'
          : 'text-gray-300';
  return (
    <div>
      <p className="text-gray-500">{etykieta}</p>
      <p className={`${klasa} break-words`}>{wartosc}</p>
      {pod && <p className="text-xs text-gray-600 break-words">{pod}</p>}
    </div>
  );
}

export function PanelDiagnostics({
  licenseId,
  puls,
  heartbeatAt,
}: {
  licenseId: string;
  puls: PanelPulsFacts;
  heartbeatAt: string | null;
}) {
  const [historia, setHistoria] = useState<Diagnostyka | null>(null);
  const [laduje, setLaduje] = useState(false);
  const [blad, setBlad] = useState<string | null>(null);
  const [dziennik, setDziennik] = useState<WpisDziennika[] | null>(null);
  const [ladujeDziennik, setLadujeDziennik] = useState(false);
  const [zlecenia, setZlecenia] = useState<Zlecenie[] | null>(null);
  const [zlecana, setZlecana] = useState<string | null>(null);

  const wiekS = heartbeatAt ? (Date.now() - new Date(heartbeatAt).getTime()) / 1000 : null;
  const zywy = wiekS != null && wiekS < CISZA_MIN * 60;
  const milczy = wiekS == null || wiekS >= MILCZY_MIN * 60;
  const StatusIkona = zywy ? CheckCircle2 : milczy ? AlertTriangle : Activity;
  const statusKlasa = zywy ? 'text-emerald-400' : milczy ? 'text-red-400' : 'text-amber-400';
  const statusNapis = zywy ? 'Bije' : milczy ? 'Milczy' : 'Cichnie';

  const naSymulatorze = puls.waga?.zrodlo === 'symulator';
  // `undefined` = panel starszy niż `1.0.70`, a nie „połączony". Trzeci stan,
  // ta sama reguła co przy `bledy`.
  const polaczenieAgenta = puls.waga?.polaczenie;
  const wersjaAgenta = puls.waga?.wersja_agenta;
  const automat = puls.aktualizacja?.automat;
  const p95 = puls.klatki?.p95_ms;
  const budzet = puls.klatki?.budzet_ms ?? 16;
  const klatkiPonad = typeof p95 === 'number' && p95 > budzet;
  const dopasowanie = puls.ekran?.dopasowanie;
  const sciesniety = typeof dopasowanie === 'number' && dopasowanie < 0.999;
  const rozjazd = puls.zegar?.rozjazd_s;
  const zegarZly = typeof rozjazd === 'number' && Math.abs(rozjazd) >= ROZJAZD_PROG_S;
  const szczebel = puls.lacznosc?.szczebel;
  const wKolejce = puls.lacznosc?.w_kolejce ?? 0;

  // `bledy` nieobecne = starsza binarka, nie „zero błędów". Patrz komentarz
  // przy `PanelPulsFacts.bledy`.
  const umieOBledach = puls.bledy != null;
  const bledow = puls.bledy?.w_24h ?? 0;
  const wywrotek = puls.bledy?.wywrotek_startu ?? 0;
  const bleOpis = opisBle(puls.ble);

  const pobierzHistorie = async () => {
    setLaduje(true);
    setBlad(null);
    const { data, error } = await supabase.rpc('fabrica_panel_diagnostyka', {
      p_license_id: licenseId,
    });
    setLaduje(false);
    if (error) {
      setBlad(error.message);
      return;
    }
    setHistoria(data as Diagnostyka);
  };

  // ZLECENIE SONDY. Wysyła WYŁĄCZNIE NAZWĘ z białej listy — bez parametrów.
  // To nie jest kanał zdalnego wykonania i nie ma nim być: parametr znaczyłby,
  // że przejęte konto administratora steruje pudełkiem w hali.
  const zlecSonde = async (sonda: string) => {
    setZlecana(sonda);
    setBlad(null);
    const { error } = await supabase.rpc('fabrica_panel_zlec_sonde', {
      p_license_id: licenseId,
      p_sonda: sonda,
    });
    setZlecana(null);
    if (error) {
      setBlad(error.message);
      return;
    }
    await pobierzZlecenia();
  };

  const pobierzZlecenia = async () => {
    const { data, error } = await supabase
      .from('fabrica_panel_zlecenia')
      .select('id, sonda, stan, zlecono, odpowiedziano, odpowiedz, blad')
      .eq('license_id', licenseId)
      .order('zlecono', { ascending: false })
      .limit(12);
    if (error) {
      setBlad(error.message);
      return;
    }
    setZlecenia((data ?? []) as Zlecenie[]);
  };

  // DZIENNIK JEST ZA KLIKNIĘCIEM, tak samo jak historia i z tego samego powodu:
  // „pokaż mi stos" jest pytaniem, które się ZADAJE, a nie tłem, które się
  // ogląda. Dwadzieścia cztery stanowiska znaczyłyby dwadzieścia cztery
  // zapytania puszczone dlatego, że ktoś wszedł na listę.
  const pobierzDziennik = async () => {
    setLadujeDziennik(true);
    setBlad(null);
    const { data, error } = await supabase
      .from('fabrica_panel_dziennik')
      .select('id, o, przyjeto, zrodlo, waga, skad, co, slad, wersja')
      // `przyjeto`, nie `o`: stanowisko z rozjechanym zegarem potrafi datować
      // wpisy w przyszłość, a wtedy sortowanie po `o` stawia je na wierzchu
      // niezależnie od tego, kiedy naprawdę doszły.
      .order('przyjeto', { ascending: false })
      .eq('license_id', licenseId)
      .limit(80);
    setLadujeDziennik(false);
    if (error) {
      setBlad(error.message);
      return;
    }
    setDziennik((data ?? []) as WpisDziennika[]);
  };

  const rozdzielczosc =
    puls.ekran?.px?.w && puls.ekran?.px?.h
      ? `${Math.round(puls.ekran.px.w)}×${Math.round(puls.ekran.px.h)}`
      : '—';

  return (
    <div className="mt-4 pt-4 border-t border-gray-700">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <StatusIkona className={`w-4 h-4 ${statusKlasa}`} />
        <span className={`text-sm font-medium ${statusKlasa}`}>{statusNapis}</span>
        <span className="flex items-center gap-1 text-xs text-gray-500">
          <Clock className="w-3 h-3" />
          {odKiedy(heartbeatAt)}
        </span>
        <span className="text-xs text-gray-600">panel produkcyjny</span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <Fakt
          etykieta="Wersja"
          wartosc={puls.wersja ?? '—'}
          pod={puls.kod_wersji != null ? `kod ${puls.kod_wersji}` : undefined}
        />
        <Fakt etykieta="System" wartosc={puls.system ?? '—'} pod={puls.platforma} />
        <Fakt etykieta="Czas pracy" wartosc={czasTrwania(puls.czas_pracy_s)} />
        <Fakt
          etykieta="Źródło masy"
          // NAJWAŻNIEJSZE POLE CAŁEGO PULSU (`puls.dart`): „stanowisko rysuje
          // symulator" jest usterką produkcyjną, która nie boli, dopóki ktoś
          // nie sprawdzi rekordu partii.
          wartosc={naSymulatorze ? 'symulator' : (puls.waga?.zrodlo ?? '—')}
          ton={naSymulatorze ? 'uwaga' : 'dobry'}
          pod={
            puls.waga?.protokol ??
            (puls.waga?.token_agenta ? `token: ${puls.waga.token_agenta}` : undefined)
          }
        />

        <Fakt etykieta="Rozdzielczość" wartosc={rozdzielczosc} />
        <Fakt
          etykieta="DPI"
          wartosc={puls.ekran?.dpi != null ? String(puls.ekran.dpi) : '—'}
          pod={puls.ekran?.zrodlo_dpi ? `z: ${puls.ekran.zrodlo_dpi}` : undefined}
        />
        <Fakt
          etykieta="Miara"
          wartosc={
            dopasowanie == null
              ? '—'
              : sciesniety
                ? `${Math.round(dopasowanie * 100)} % — ściśnięta`
                : 'pełna'
          }
          ton={sciesniety ? 'uwaga' : 'zwykly'}
        />
        <Fakt
          etykieta="Klatki p95"
          wartosc={p95 != null ? `${p95} ms` : '—'}
          ton={klatkiPonad ? 'uwaga' : 'zwykly'}
          pod={
            puls.klatki?.zmierzonych != null
              ? `${puls.klatki.ponad_budzet ?? 0} / ${puls.klatki.zmierzonych} ponad ${budzet} ms`
              : undefined
          }
        />

        {/* ROZMOWA Z AGENTEM to inne pytanie niż ŹRÓDŁO MASY wyżej. `zrodlo:
            agent` znaczy „panel ma token"; czy uścisk dłoni doszedł do skutku,
            mówi dopiero to pole (od `1.0.70`). Stanowisko 3 wysyłało
            26.08.2026 `agent` przy ekranie „Agent stanowiska nie odpowiada". */}
        <Fakt
          etykieta="Rozmowa z agentem"
          wartosc={NAZWA_POLACZENIA[polaczenieAgenta ?? ''] ?? polaczenieAgenta ?? 'nie podaje'}
          ton={
            polaczenieAgenta == null || polaczenieAgenta === 'nieProbowano'
              ? 'zwykly'
              : polaczenieAgenta === 'polaczony'
                ? 'dobry'
                : 'zly'
          }
          pod={
            polaczenieAgenta === 'odmowaTokenu'
              ? 'panel i agent mają różne sekrety'
              : polaczenieAgenta === 'brakNasluchu'
                ? 'usługa agenta nie wstała albo padła'
                : undefined
          }
        />

        {/* AGENT W INNEJ WERSJI NIŻ PANEL. Oba jadą jednym instalatorem pod
            jednym numerem, więc dwie różne liczby znaczą jedno: agent nie
            wstał z nową paczką. Na Pi do 1.0.82 działo się to po KAŻDYM OTA
            (`systemctl enable --now` nie restartuje działającej usługi)
            i nie było tego widać znikąd — agent meldował stały literał. */}
        <Fakt
          etykieta="Agent wagi"
          wartosc={wersjaAgenta ?? 'nie podaje'}
          ton={wersjaAgenta == null ? 'zwykly' : wersjaAgenta === puls.wersja ? 'dobry' : 'uwaga'}
          pod={
            wersjaAgenta != null && wersjaAgenta !== puls.wersja
              ? `panel ${puls.wersja ?? '?'} — agent nie wstał po aktualizacji`
              : wersjaAgenta != null
                ? 'ta sama paczka co panel'
                : undefined
          }
        />

        {/* AUTOMAT AKTUALIZACJI — czy wydania w ogóle mają czym wejść.
            Stanowisko z martwym automatem wyglądało do 1.0.82 jak stanowisko,
            na którym nikt nie włączył aktualizacji; obie te rzeczy to inna
            wizyta serwisanta. */}
        <Fakt
          etykieta="Automat aktualizacji"
          wartosc={NAZWA_AUTOMATU[automat ?? ''] ?? automat ?? 'nie podaje'}
          ton={
            automat == null || automat === 'nieDotyczy'
              ? 'zwykly'
              : automat === 'zyje' || automat === 'usluga'
                ? 'dobry'
                : automat === 'martwy'
                  ? 'zly'
                  : 'uwaga'
          }
          pod={
            puls.aktualizacja?.tetno_s != null
              ? `tętno ${czasTrwania(puls.aktualizacja.tetno_s)} temu`
              : automat === 'brak'
                ? 'wydania nie wejdą same — instalator z ręki zakłada zadanie'
                : undefined
          }
        />

        <Fakt
          etykieta="Łączność"
          // Brak pola to BRAK WIEDZY, nie pełnia — panel sprzed `1.0.43` o tym
          // nie mówił i udawanie, że milczenie znaczy „dobrze", byłoby
          // wymyśleniem pomiaru.
          wartosc={szczebel ? (NAZWA_SZCZEBLA[szczebel] ?? szczebel) : 'nie podaje'}
          ton={szczebel && szczebel !== 'pelnia' ? 'uwaga' : 'zwykly'}
          pod={
            puls.lacznosc?.cisza_s != null
              ? `cisza ${czasTrwania(puls.lacznosc.cisza_s)}`
              : undefined
          }
        />
        <Fakt
          etykieta="Kolejka zapisów"
          wartosc={
            puls.lacznosc ? `${wKolejce}${puls.lacznosc.kolejka_urwana ? '+' : ''}` : 'nie podaje'
          }
          ton={wKolejce > 0 ? 'uwaga' : 'zwykly'}
        />
        <Fakt
          etykieta="Zegar"
          wartosc={
            rozjazd == null
              ? 'nie podaje'
              : rozjazd === 0
                ? 'zgodny'
                : `${rozjazd > 0 ? '+' : ''}${rozjazd} s`
          }
          ton={zegarZly ? 'uwaga' : 'zwykly'}
          pod={puls.zegar?.strefa}
        />
        {/*
          BŁĘDY. Dwa osobne fakty, bo to dwie różne wizyty serwisanta:
          „panel działa i pluje błędami" kontra „panel nie wstaje". Licznik
          wywrotek odpowiada na pytanie, którego `last_heartbeat_at` nie
          słyszy — milczenie i pętla restartów wyglądają z serwera identycznie.
        */}
        <Fakt
          etykieta="Błędy (24 h)"
          wartosc={!umieOBledach ? 'nie podaje' : bledow === 0 ? 'brak' : String(bledow)}
          ton={bledow > 0 ? 'uwaga' : 'zwykly'}
          pod={
            puls.bledy?.ostatni?.co
              ? `${puls.bledy.ostatni.skad ?? '?'}: ${puls.bledy.ostatni.co}`
              : undefined
          }
        />
        <Fakt
          etykieta="Wywrotki startu"
          wartosc={!umieOBledach ? 'nie podaje' : wywrotek === 0 ? 'wstał' : String(wywrotek)}
          ton={wywrotek > 0 ? 'uwaga' : 'zwykly'}
          pod={wywrotek > 0 ? 'nie doszedł do pierwszej klatki' : undefined}
        />
        {/*
          RADIO BLUETOOTH. Do 21.09.2026 puls niósł wyłącznie `zdolnosci: []`
          i serwer nie miał jak odróżnić czterech przyczyn, z których każda ma
          inną naprawę — a jedna z nich („paczka agenta bez stosu") jest
          usterką WYDANIA i żaden dongiel jej nie naprawia. Werdykt liczy
          `opisBle`, wspólny z ekranem floty.
        */}
        <Fakt
          etykieta="Radio Bluetooth"
          wartosc={bleOpis.wartosc}
          ton={bleOpis.ton}
          pod={bleOpis.pod}
        />

        <Fakt
          etykieta="Stoi na"
          wartosc={
            puls.praca?.ekran ? (NAZWA_EKRANU[puls.praca.ekran] ?? puls.praca.ekran) : 'nie podaje'
          }
          pod={
            puls.praca
              ? [
                  puls.praca.zalogowany ? 'operator zalogowany' : 'nikt niezalogowany',
                  puls.praca.w_zadaniu ? 'zadanie wzięte' : null,
                  puls.praca.odliczan ? `${puls.praca.odliczan} odliczań` : null,
                ]
                  .filter(Boolean)
                  .join(' · ')
              : undefined
          }
        />

        <Fakt
          etykieta="Pamięć"
          wartosc={puls.pamiec_mb != null ? `${puls.pamiec_mb} MB` : '—'}
          pod={puls.rdzenie != null ? `${puls.rdzenie} rdzeni` : undefined}
        />
        <Fakt etykieta="Dart" wartosc={puls.dart ?? '—'} />
        <Fakt etykieta="Klasa urządzenia" wartosc={puls.device_class ?? '—'} />
      </div>

      {/* ── HISTORIA ─────────────────────────────────────────────────────── */}
      {historia == null ? (
        <button
          type="button"
          onClick={pobierzHistorie}
          disabled={laduje}
          className="mt-4 inline-flex items-center gap-2 text-xs text-gray-400 hover:text-gray-200 disabled:opacity-50"
        >
          {laduje ? <Loader2 className="w-3 h-3 animate-spin" /> : <History className="w-3 h-3" />}
          {laduje ? 'liczę szereg…' : 'historia z ostatnich 30 dni'}
        </button>
      ) : (
        <div className="mt-4 pt-3 border-t border-gray-800">
          <p className="text-xs text-gray-500 mb-3">
            {historia.pulsow} pulsów, od {dataGodzina(historia.pierwszy)}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <Fakt
              etykieta="Restarty (24 h)"
              wartosc={String(historia.restartow_24h)}
              ton={historia.restartow_24h > 3 ? 'uwaga' : 'zwykly'}
              pod={`7 dni: ${historia.restartow_7d}`}
            />
            <Fakt
              etykieta="Najdłuższa praca"
              // Stanowisko, które NIGDY nie przekroczyło kwadransa, jest w pętli
              // restartów — choć każdy pojedynczy puls wygląda niewinnie.
              wartosc={czasTrwania(historia.czas_pracy_rekord_s)}
              ton={
                historia.czas_pracy_rekord_s != null && historia.czas_pracy_rekord_s < 900
                  ? 'uwaga'
                  : 'zwykly'
              }
              pod={
                historia.ostatni_restart
                  ? `ostatni restart ${odKiedy(historia.ostatni_restart)}`
                  : undefined
              }
            />
            <Fakt
              etykieta="Przerwy (7 dni)"
              wartosc={String(historia.cisz_7d)}
              ton={historia.cisz_7d > 0 ? 'uwaga' : 'zwykly'}
              pod={
                historia.cisza_max_s != null
                  ? `najdłuższa ${czasTrwania(historia.cisza_max_s)}`
                  : undefined
              }
            />
            <Fakt
              etykieta="Na agencie (7 dni)"
              wartosc={`${historia.na_agencie_7d} / ${historia.na_agencie_7d + historia.na_symulatorze_7d}`}
              ton={historia.na_agencie_7d === 0 ? 'uwaga' : 'zwykly'}
              pod={
                historia.ostatnio_na_agencie
                  ? `ostatnio ${odKiedy(historia.ostatnio_na_agencie)}`
                  : 'nigdy nie widział wagi'
              }
            />
            <Fakt
              etykieta="p95 średni (7 dni)"
              wartosc={historia.p95_sredni_7d != null ? `${historia.p95_sredni_7d} ms` : '—'}
              ton={
                historia.p95_sredni_7d != null && historia.p95_sredni_7d > budzet
                  ? 'uwaga'
                  : 'zwykly'
              }
              pod={
                historia.p95_najgorszy_7d != null
                  ? `najgorszy ${historia.p95_najgorszy_7d} ms`
                  : undefined
              }
            />
            <Fakt
              etykieta="Szczyt pamięci"
              wartosc={historia.pamiec_szczyt_mb != null ? `${historia.pamiec_szczyt_mb} MB` : '—'}
            />
            <Fakt
              etykieta="Rozjazd zegara"
              wartosc={
                historia.rozjazd_max_s != null ? `max ${historia.rozjazd_max_s} s` : 'nie podaje'
              }
              ton={
                historia.rozjazd_max_s != null && historia.rozjazd_max_s >= ROZJAZD_PROG_S
                  ? 'uwaga'
                  : 'zwykly'
              }
            />
            <Fakt
              etykieta="Bez łączności (7 dni)"
              wartosc={
                historia.samotnosci_7d + historia.przycichniec_7d > 0
                  ? `${historia.samotnosci_7d} + ${historia.przycichniec_7d}`
                  : 'nie podaje'
              }
              ton={historia.samotnosci_7d > 0 ? 'uwaga' : 'zwykly'}
              pod="samotność + przycichnięcie"
            />
          </div>

          {historia.wersje.length > 0 && (
            <div className="mt-4">
              <p className="text-gray-500 text-sm mb-1">Wersje na tym stanowisku</p>
              <div className="flex flex-wrap gap-2">
                {historia.wersje.map((w) => (
                  <span
                    key={`${w.wersja}-${w.kod_wersji ?? '?'}`}
                    className="mono text-xs px-2 py-1 rounded bg-gray-800 text-gray-300"
                    title={`${dataGodzina(w.od)} → ${dataGodzina(w.do)} · ${w.pulsow} pulsów`}
                  >
                    {w.wersja}
                  </span>
                ))}
              </div>
            </div>
          )}

          {historia.cisze.length > 0 && (
            <div className="mt-4">
              <p className="text-gray-500 text-sm mb-1">Najdłuższe przerwy</p>
              <div className="space-y-1">
                {historia.cisze.slice(0, 5).map((c) => (
                  <p key={c.od} className="mono text-xs text-gray-400">
                    {dataGodzina(c.od)} → {dataGodzina(c.do)}{' '}
                    <span className="text-amber-400">{czasTrwania(c.trwala_s)}</span>
                  </p>
                ))}
              </div>
            </div>
          )}

          {Object.keys(historia.ekrany_7d).length > 0 && (
            <div className="mt-4">
              <p className="text-gray-500 text-sm mb-1">Na czym stało (7 dni)</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(historia.ekrany_7d)
                  .sort((a, b) => b[1] - a[1])
                  .map(([ekran, ile]) => (
                    <span
                      key={ekran}
                      className="text-xs px-2 py-1 rounded bg-gray-800 text-gray-300"
                    >
                      {NAZWA_EKRANU[ekran] ?? ekran} · {ile}
                    </span>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── DZIENNIK ─────────────────────────────────────────────────────── */}
      {dziennik == null ? (
        <button
          type="button"
          onClick={pobierzDziennik}
          disabled={ladujeDziennik}
          className="mt-3 inline-flex items-center gap-2 text-xs text-gray-400 hover:text-gray-200 disabled:opacity-50"
        >
          {ladujeDziennik ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <ScrollText className="w-3 h-3" />
          )}
          {ladujeDziennik ? 'czytam dziennik…' : 'dziennik stanowiska'}
        </button>
      ) : (
        <div className="mt-4 pt-3 border-t border-gray-800">
          {dziennik.length === 0 ? (
            // PUSTY DZIENNIK NIE JEST BŁĘDEM I NIE MA UDAWAĆ BŁĘDU. Panel
            // sprzed 1.0.68 nic nie przysyła, panel zdrowy nie ma o czym —
            // i te dwa stany rozróżnia numer wersji obok, nie ten napis.
            <p className="text-xs text-gray-500">
              Brak wpisów. Stanowisko nic nie zgłosiło albo stoi na wersji sprzed 1.0.68.
            </p>
          ) : (
            <div className="space-y-1 max-h-96 overflow-y-auto">
              {dziennik.map((w) => (
                <div key={w.id} className="text-xs leading-snug">
                  <span className="mono text-gray-500">{dataGodzina(w.przyjeto)}</span>{' '}
                  <span
                    className={
                      w.zrodlo === 'agent'
                        ? 'px-1 rounded bg-sky-900/60 text-sky-300'
                        : 'px-1 rounded bg-gray-800 text-gray-400'
                    }
                  >
                    {w.zrodlo}
                  </span>{' '}
                  {w.skad && <span className="text-gray-500">{w.skad}</span>}{' '}
                  <span className={w.waga === 'blad' ? 'text-red-400' : 'text-gray-300'}>
                    {w.co}
                  </span>
                  {w.slad && (
                    // Stos jest ZWINIĘTY. Rozwinięty zajmuje ekran i spycha
                    // z pola widzenia wpisy sąsiednie — a to one mówią, co
                    // panel wtedy robił.
                    <details className="ml-4">
                      <summary className="cursor-pointer text-gray-600 hover:text-gray-400">
                        stos
                      </summary>
                      <pre className="mono text-[10px] text-gray-500 whitespace-pre-wrap overflow-x-auto">
                        {w.slad}
                      </pre>
                    </details>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── ZAPYTAJ STANOWISKO ───────────────────────────────────────────── */}
      <div className="mt-4 pt-3 border-t border-gray-800">
        <p className="text-xs text-gray-500 mb-2">
          Zapytaj stanowisko — odpowiedź wraca w ciągu minuty, o ile panel żyje. Zlecenie niepodjęte
          przez godzinę gaśnie samo.
        </p>
        <div className="flex flex-wrap gap-1.5">
          {SONDY.map((s) => (
            <button
              key={s.nazwa}
              type="button"
              title={s.opis}
              onClick={() => zlecSonde(s.nazwa)}
              disabled={zlecana != null}
              className={`text-xs px-2 py-1 rounded border disabled:opacity-40 ${
                s.drogo
                  ? 'border-amber-800 text-amber-300 hover:bg-amber-950'
                  : 'border-gray-700 text-gray-300 hover:bg-gray-800'
              }`}
            >
              {zlecana === s.nazwa ? '…' : s.nazwa}
              {/* Jedyna sonda, która zajmuje magistralę — i dlatego jedyna
                  oznaczona. Przez czas jej trwania nikt przy stole nie waży. */}
              {s.drogo && <span className="ml-1 opacity-70">(zajmuje wagę)</span>}
            </button>
          ))}
          <button
            type="button"
            onClick={pobierzZlecenia}
            className="text-xs px-2 py-1 rounded text-gray-400 hover:text-gray-200"
          >
            odśwież odpowiedzi
          </button>
        </div>

        {zlecenia != null && zlecenia.length > 0 && (
          <div className="mt-3 space-y-1">
            {zlecenia.map((z) => (
              <details key={z.id} className="text-xs">
                <summary className="cursor-pointer text-gray-400 hover:text-gray-200">
                  <span className="mono text-gray-500">{dataGodzina(z.zlecono)}</span>{' '}
                  <span className="text-gray-300">{z.sonda}</span>{' '}
                  <span
                    className={
                      z.stan === 'gotowe'
                        ? 'text-emerald-400'
                        : z.stan === 'przeterminowane'
                          ? 'text-red-400'
                          : 'text-amber-400'
                    }
                  >
                    {z.stan}
                  </span>
                  {z.blad && <span className="text-red-400"> · {z.blad}</span>}
                </summary>
                <pre className="mono text-[10px] text-gray-400 whitespace-pre-wrap overflow-x-auto mt-1">
                  {z.odpowiedz ? JSON.stringify(z.odpowiedz, null, 2) : '—'}
                </pre>
              </details>
            ))}
          </div>
        )}
      </div>

      {blad && <p className="mt-3 text-xs text-red-400">Nie udało się policzyć historii: {blad}</p>}
    </div>
  );
}
